import Tabs from "@/components/Tabs";
import { Path, useNavigate } from "@/router";
import { Navigate, useLocation } from "react-router-dom";
import AppLayout from "@/layouts/AppLayout";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import Breadcrumb from "@/components/Breadcrumb";
import { usePersonErrorHandling } from "../_components/usePersonErrorHandling";
import PersonNotFoundErrorBoundary from "../_components/PersonNotFoundErrorBoundary";
import { useFlags } from "@/hooks/useFeatureFlags";
import useCapabilities, { useCanViewPersonEducationBool } from "@/hooks/useCapabilities";

export default function TabLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { pathname } = location;
  const currentUrl = pathname.replace(/\/$/, "");
  const navigate = useNavigate();
  const { isCompactLayout: isMobile } = useIsCompactLayout();
  const { pgaId, navLinks, shouldRenderError } = usePersonErrorHandling();
  const flags = useFlags();
  const academicDegreesEnabled = !!flags["backoffice-education-credentials"];
  const { loaded } = useCapabilities();
  const canViewPersonEducation = useCanViewPersonEducationBool();

  // Early return for error cases
  if (shouldRenderError) {
    return (
      <AppLayout navLinks={[]} interior bottomBar>
        <PersonNotFoundErrorBoundary />
      </AppLayout>
    );
  }

  if (loaded && !canViewPersonEducation) {
    return (
      <AppLayout navLinks={navLinks} interior bottomBar>
        <Navigate to={`/people/${pgaId}/person/overview`} replace />
      </AppLayout>
    );
  }

  const tabItems = [
    {
      label: "Professional Development (PDRs)",
      to: "/people/:pgaId/education/professional-development" as Path,
      active: currentUrl === `/people/${pgaId!}/education/professional-development`
    },
    {
      label: "Lifelong Learning",
      to: "/people/:pgaId/education/learning" as Path,
      active: currentUrl === `/people/${pgaId!}/education/learning`
    },
    {
      label: "PGM Program",
      to: "/people/:pgaId/education/program" as Path,
      active: currentUrl === `/people/${pgaId!}/education/program`
    },
    ...(academicDegreesEnabled
      ? [
          {
            label: "Academic Degrees",
            to: "/people/:pgaId/education/credentials" as Path,
            active: currentUrl === `/people/${pgaId!}/education/credentials`
          }
        ]
      : []),
    {
      label: "Certifications",
      to: "/people/:pgaId/education/certifications" as Path,
      active: currentUrl === `/people/${pgaId!}/education/certifications`
    }
  ];

  const renderDesktopLayout = () => (
    <>
      <PersonObjectHeading pgaId={pgaId!} />
      <Tabs
        tabItems={tabItems.map(({ label, to, active }) => ({
          label,
          link: to,
          onClick: () => navigate(to, { params: { pgaId: pgaId! } }),
          active
        }))}
      />
      <div className="flex flex-col" data-testid="tab-content">
        {children}
      </div>
    </>
  );

  const renderMobileLayout = () => (
    <>
      <Breadcrumb compactOnly compactTitle="Education" backLink={{ to: "/people/:pgaId", params: { pgaId: pgaId! } }} />
      <PersonObjectHeading pgaId={pgaId!} />
      <div className="mt-4 sm:mt-8">
        <Tabs
          tabItems={tabItems.map(({ label, to, active }) => ({
            label,
            link: to,
            onClick: () => navigate(to, { params: { pgaId: pgaId! } }),
            active
          }))}
        />
      </div>
      <div className="flex flex-col" data-testid="tab-content">
        {children}
      </div>
    </>
  );

  return (
    <AppLayout navLinks={navLinks} interior bottomBar>
      {isMobile ? renderMobileLayout() : renderDesktopLayout()}
    </AppLayout>
  );
}
